import RepositoryFactory from '@/services/repository-factory';

const menuItemsRepository = RepositoryFactory.get('menuItems');

function deepMergeTranslations(base = {}, patch = {}) {
  const out = { ...(base || {}) };

  Object.entries(patch || {}).forEach(([loc, v]) => {
    out[loc] = {
      ...(out[loc] || {}),
      ...(v || {}),
    };
  });

  return out;
}

function toApiPayload(src) {
  const payload = {};

  if ('icon' in src) payload.icon = src.icon || '';
  if ('url' in src) payload.url = src.url || '';
  if ('isBlank' in src) payload.is_blank = !!src.isBlank;
  if ('isEnabled' in src) payload.is_enabled = !!src.isEnabled;
  if ('isAllowedForGuest' in src) payload.is_allowed_for_guest = !!src.isAllowedForGuest;
  if ('isAllowedForAuthorized' in src) payload.is_allowed_for_authorized = !!src.isAllowedForAuthorized;
  if ('isInApp' in src) payload.is_in_app = !!src.isInApp;

  if (src.translations) {
    const t = {};

    Object.entries(src.translations).forEach(([loc, v]) => {
      if (!t[loc]) t[loc] = {};
      if (v && typeof v === 'object' && 'name' in v) t[loc].name = v.name;
    });
    if (Object.keys(t).length) payload.translations = t;
  }

  return payload;
}

function getChangeKeyFromRef({
  serverId,
  clientId,
}) {
  return clientId ? `draft:${clientId}` : `id:${serverId}`;
}

function findMenuItemIndexByRef(state, {
  serverId, clientId,
}) {
  return state.menuItems.findIndex((menuItem) => (
    clientId ? menuItem.clientId === clientId : menuItem.id === serverId));
}

export default {
  namespaced: true,

  state: {
    menuItems: [],
    unsavedChangesByKey: {},
    unsavedFlagsByKey: {},
    errors: {},
  },

  getters: {
    menuItems: (state) => state.menuItems,
    errors: (state) => state.errors,
    getMenuItemByRef:
      (state) => ({
        serverId, clientId,
      }) => state.menuItems.find((menuItem) => (
        clientId ? menuItem.clientId === clientId : menuItem.id === serverId
      )),
    hasUnsavedChangesByRef:
      (state) => ({
        serverId, clientId,
      }) => Boolean(state.unsavedFlagsByKey[getChangeKeyFromRef({
        serverId,
        clientId,
      })]),
    getUnsavedChangesByRef:
      (state) => ({
        serverId,
        clientId,
      }) => state.unsavedChangesByKey[getChangeKeyFromRef({
        serverId,
        clientId,
      })] || {},
    unsavedChangeKeys: (state) => Object.keys(state.unsavedFlagsByKey),
  },

  mutations: {
    SET_MENU_ITEMS(state, menuItems) {
      state.menuItems = menuItems;
    },

    PATCH_MENU_ITEM_LOCAL(state, {
      serverId,
      clientId,
      patch,
    }) {
      const menuItemIndex = findMenuItemIndexByRef(state, {
        serverId,
        clientId,
      });

      if (menuItemIndex === -1) return;

      const currentItem = state.menuItems[menuItemIndex];
      const updatedItem = {
        ...currentItem,
        ...patch,
      };

      state.menuItems.splice(menuItemIndex, 1, updatedItem);

      const changeKey = getChangeKeyFromRef({
        serverId,
        clientId,
      });
      const previousChanges = state.unsavedChangesByKey[changeKey] || {};

      state.unsavedChangesByKey = {
        ...state.unsavedChangesByKey,
        [changeKey]: {
          ...previousChanges,
          ...patch,
        },
      };
      state.unsavedFlagsByKey = {
        ...state.unsavedFlagsByKey,
        [changeKey]: true,
      };
    },

    SET_TRANSLATION_NAME_LOCAL(state, {
      serverId, clientId, locale, name,
    }) {
      const menuItemIndex = findMenuItemIndexByRef(state, {
        serverId,
        clientId,
      });

      if (menuItemIndex === -1) return;

      const currentItem = state.menuItems[menuItemIndex];
      const nextTranslations = { ...(currentItem.translations || {}) };

      nextTranslations[locale] = {
        ...(nextTranslations[locale] || {}),
        name,
      };
      state.menuItems.splice(menuItemIndex, 1, {
        ...currentItem,
        translations: nextTranslations,
      });

      const changeKey = getChangeKeyFromRef({
        serverId,
        clientId,
      });
      const previousItemChanges = state.unsavedChangesByKey[changeKey] || {};
      const previousTranslations = previousItemChanges.translations || {};

      state.unsavedChangesByKey = {
        ...state.unsavedChangesByKey,
        [changeKey]: {
          ...previousItemChanges,
          translations: {
            ...previousTranslations,
            [locale]: {
              ...(previousTranslations[locale] || {}),
              name,
            },
          },
        },
      };
      state.unsavedFlagsByKey = {
        ...state.unsavedFlagsByKey,
        [changeKey]: true,
      };
    },

    REMOVE_ITEM_LOCAL(state, {
      serverId, clientId,
    }) {
      state.menuItems = state.menuItems.filter((menuItem) => (
        clientId ? menuItem.clientId !== clientId : menuItem.id !== serverId
      ));

      const changeKey = getChangeKeyFromRef({
        serverId,
        clientId,
      });
      const remainingUnsavedChangesByKey = { ...state.unsavedChangesByKey };

      delete remainingUnsavedChangesByKey[changeKey];
      state.unsavedChangesByKey = remainingUnsavedChangesByKey;

      const remainingUnsavedFlagsByKey = { ...state.unsavedFlagsByKey };

      delete remainingUnsavedFlagsByKey[changeKey];
      state.unsavedFlagsByKey = remainingUnsavedFlagsByKey;
    },

    RESET_ITEM_CHANGES(state, {
      serverId,
      clientId,
    }) {
      const changeKey = getChangeKeyFromRef({
        serverId,
        clientId,
      });
      const remainingUnsavedChangesByKey = { ...state.unsavedChangesByKey };

      delete remainingUnsavedChangesByKey[changeKey];
      state.unsavedChangesByKey = remainingUnsavedChangesByKey;

      const remainingUnsavedFlagsByKey = { ...state.unsavedFlagsByKey };

      delete remainingUnsavedFlagsByKey[changeKey];
      state.unsavedFlagsByKey = remainingUnsavedFlagsByKey;
    },

    RESET_ALL_CHANGES(state) {
      state.unsavedChangesByKey = {};
      state.unsavedFlagsByKey = {};
    },

    ADD_DRAFT_ITEM(state, draftItem) {
      state.menuItems = [...state.menuItems, draftItem];
    },

    REPLACE_ITEM_BY_REF(state, {
      serverId, clientId, item,
    }) {
      const idx = findMenuItemIndexByRef(state, {
        serverId,
        clientId,
      });

      if (idx === -1) return;
      state.menuItems.splice(idx, 1, item);
    },

    SET_ERRORS(state, errors) {
      state.errors = errors || {};
    },
  },

  actions: {
    async list({ commit }) {
      const menuItems = await menuItemsRepository.list();

      commit('SET_MENU_ITEMS', menuItems);
    },

    patchLocalField({ commit }, {
      serverId = null,
      clientId = null,
      patch,
    }) {
      commit('PATCH_MENU_ITEM_LOCAL', {
        serverId,
        clientId,
        patch,
      });
    },

    setLocalizedName(
      { commit },
      {
        serverId = null,
        clientId = null,
        locale, name,
      },
    ) {
      commit('SET_TRANSLATION_NAME_LOCAL', {
        serverId,
        clientId,
        locale,
        name,
      });
    },

    addDraftItem({ commit }, { initialLocale = 'en' } = {}) {
      const clientId = `draft_${Date.now()}`;
      const draftItem = {
        id: null,
        clientId,
        isDraft: true,
        icon: '',
        url: '',
        isBlank: false,
        isEnabled: true,
        isAllowedForAuthorized: false,
        isAllowedForGuest: false,
        isInApp: false,
        name: '',
        translations: { [initialLocale]: { name: '' } },
      };

      commit('ADD_DRAFT_ITEM', draftItem);

      return clientId;
    },

    async removeItem({
      commit,
      dispatch,
      state,
    }, {
      serverId = null,
      clientId = null,
    }) {
      if (!serverId) {
        commit('REMOVE_ITEM_LOCAL', {
          serverId,
          clientId,
        });

        return;
      }

      try {
        await menuItemsRepository.remove(serverId);
        dispatch('list');
      } catch (error) {
        commit('SET_ERRORS', {
          ...state.errors,
          [serverId]: error?.message || 'Delete failed',
        });
      }
    },

    async saveItem({
      getters, commit, state,
    }, {
      serverId = null,
      clientId = null,
    }) {
      try {
        const current = getters.getMenuItemByRef({
          serverId,
          clientId,
        }) || {};
        const unsaved = getters.getUnsavedChangesByRef({
          serverId,
          clientId,
        }) || {};
        const merged = {
          ...current,
          ...unsaved,
          translations: deepMergeTranslations(current.translations, unsaved.translations),
        };
        const payload = toApiPayload(merged);

        if (!Object.keys(payload).length) {
          commit('RESET_ITEM_CHANGES', {
            serverId,
            clientId,
          });

          return;
        }

        if (serverId) {
          const updated = await menuItemsRepository.update(serverId, payload);

          commit('REPLACE_ITEM_BY_REF', {
            serverId,
            clientId,
            item: updated,
          });
          commit('RESET_ITEM_CHANGES', {
            serverId,
            clientId,
          });

          const key = `id:${serverId}`;
          const next = { ...state.errors };

          delete next[key];
          commit('SET_ERRORS', next);
        } else {
          const created = await menuItemsRepository.create(payload);

          commit('REPLACE_ITEM_BY_REF', {
            serverId: null,
            clientId,
            item: created,
          });
          commit('RESET_ITEM_CHANGES', {
            serverId: created.id,
            clientId: null,
          });

          const key = `draft:${clientId}`;
          const next = { ...state.errors };

          delete next[key];
          commit('SET_ERRORS', next);
        }
      } catch (error) {
        const fields = error.errors || error?.response?.data?.errors || {};
        const key = serverId ? `id:${serverId}` : `draft:${clientId}`;

        commit('SET_ERRORS', {
          ...state.errors,
          [key]: fields,
        });
      }
    },
  },
};
